/** Empty-state overlay: sparkle, heading, a "Try asking" starter list, and a "Browse topics" category
 *  accordion. Every prompt pre-fills the composer (never auto-sends); mirrors the ai-teammate drawer. */

import type { MouseEvent } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Box, Button, Chip, Collapse, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { AutoAwesome } from "@mui/icons-material"
import { useChatComposer } from "@mui/x-chat-headless"
import { PROMPT_CATEGORIES, STARTER_PROMPTS } from "../constants"
import { COMPOSER_CLEARANCE, CONVERSATION_COLUMN_MAX_WIDTH } from "./composer-chrome"

// position:absolute takes the height from ChatBox's empty-state overlay (inset:0, positioned) instead of the
// unsized flex wrapper in between — a percentage height there resolves to auto, so overflowY never engages.
const ROOT_SX = {
  position: "absolute",
  inset: 0,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  px: 3,
  pt: 3,
  // Centres the block in the band above the composer rather than behind it.
  pb: `calc(${COMPOSER_CLEARANCE} + 1rem)`,
} satisfies SxProps<Theme>
// m:"auto" centers the block when there's spare height and collapses to 0 when content overflows, keeping the top reachable.
const CONTENT_SX = { m: "auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1.5, width: "100%", maxWidth: CONVERSATION_COLUMN_MAX_WIDTH } satisfies SxProps<Theme>
const ICON_SX = { fontSize: "2.5rem" } satisfies SxProps<Theme>
const HEADING_SX = { fontWeight: 700 } satisfies SxProps<Theme>
const SUBTITLE_SX = { color: "text.secondary" } satisfies SxProps<Theme>
const SECTION_SX = { display: "flex", flexDirection: "column", gap: 1, width: "100%", mt: 1 } satisfies SxProps<Theme>
const LABEL_SX = { color: "text.secondary", textAlign: "left", letterSpacing: "0.08em" } satisfies SxProps<Theme>
const PROMPT_LIST_SX = { display: "flex", flexDirection: "column", gap: 1, width: "100%" } satisfies SxProps<Theme>
const PROMPT_SX = { justifyContent: "flex-start", textAlign: "left", textTransform: "none" } satisfies SxProps<Theme>
const CHIPS_SX = { display: "flex", flexWrap: "wrap", gap: 0.75 } satisfies SxProps<Theme>

export function ChatEmptyState() {
  const composer = useChatComposer()
  const [expandedTheme, setExpandedTheme] = useState<string | null>(null)
  // Retain the last opened theme so its prompts stay rendered through the Collapse close animation.
  const [lastExpandedTheme, setLastExpandedTheme] = useState<string | null>(null)
  const expandedListRef = useRef<HTMLDivElement>(null)
  // Switching straight from one open topic to another keeps the Collapse `in`, so onEntered never fires for the
  // replacement list — reveal it here. onEntered still covers the closed→open case, where this runs mid-transition.
  // Optional call: jsdom implements no scrollIntoView, and this is browser geometry the story asserts instead.
  useEffect(() => {
    if (expandedTheme != null) expandedListRef.current?.scrollIntoView?.({ block: "nearest" })
  }, [expandedTheme])

  const pickPrompt = useCallback((event: MouseEvent<HTMLButtonElement>, prompt: string) => {
    composer.setValue(prompt)
    // useChatComposer has no focus(); reach the sibling textarea via the shared ChatBox root.
    const textarea = event.currentTarget.closest(".MuiChatBox-root")?.querySelector("textarea")
    textarea?.focus()
  }, [composer])

  // Accordion toggle: clicking the open chip closes it (drawer parity — the toggle is the reset affordance).
  const toggleCategory = useCallback((theme: string) => {
    setExpandedTheme((current) => (current === theme ? null : theme))
    setLastExpandedTheme(theme)
  }, [])

  const expandedCategory = PROMPT_CATEGORIES.find((category) => category.theme === expandedTheme) ?? null
  const categoryForRender = expandedCategory ?? PROMPT_CATEGORIES.find((category) => category.theme === lastExpandedTheme) ?? null

  return <Box sx={ROOT_SX} data-testid="chatbox-empty-state">
    <Box sx={CONTENT_SX}>
      <AutoAwesome color="primary" sx={ICON_SX} aria-hidden />
      <Typography variant="h5" component="h2" sx={HEADING_SX}>How can I help?</Typography>
      <Typography variant="body1" sx={SUBTITLE_SX}>
        Ask about your portfolios, performance, or risk. Answers cite Omega Point analytics.
      </Typography>

      <Box sx={SECTION_SX} component="section" aria-label="Try asking">
        <Typography variant="overline" sx={LABEL_SX}>Try asking</Typography>
        <Box sx={PROMPT_LIST_SX}>
          {STARTER_PROMPTS.map((prompt) =>
            <Button key={prompt} variant="outlined" sx={PROMPT_SX} onClick={(event) => pickPrompt(event, prompt)}>
              {prompt}
            </Button>)}
        </Box>
      </Box>

      <Box sx={SECTION_SX} component="section" aria-label="Browse topics">
        <Typography variant="overline" sx={LABEL_SX}>Browse topics</Typography>
        <Box sx={CHIPS_SX}>
          {PROMPT_CATEGORIES.map((category) => {
            const isOpen = expandedTheme === category.theme
            return <Chip
              key={category.theme}
              label={category.label}
              onClick={() => toggleCategory(category.theme)}
              color={isOpen ? "primary" : "default"}
              variant={isOpen ? "filled" : "outlined"}
              aria-expanded={isOpen}
            />
          })}
        </Box>
        <Collapse in={!!expandedCategory} unmountOnExit onEntered={() => expandedListRef.current?.scrollIntoView?.({ block: "nearest" })}>
          {categoryForRender && (
            <Box ref={expandedListRef} sx={PROMPT_LIST_SX}>
              {categoryForRender.prompts.map((prompt) =>
                <Button key={prompt} variant="outlined" sx={PROMPT_SX} onClick={(event) => pickPrompt(event, prompt)}>
                  {prompt}
                </Button>)}
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  </Box>
}
